import React, { useEffect, useState } from 'react';
import styles from './Results.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getResults, clearMatchResults } from '../../store/actions/eventResults';
import { useEffectSkipFirst } from '../../hooks/useEffectSkipFirst';

import ContentSpinner from '../shared/Spinner/Spinner';
import ResultsTable from './Table/Table';
import Title from '../shared/Title/Title';
import ResultsFilter from './Filters/Filters';
import moment from 'moment';
import { getTranslation } from '../../utils/translation';
import useOnScreen from '../../hooks/useOnScreen';

const periods = {
    lastTwelveHours: {
        value: getTranslation('filtersRanges12Hours'),
        key: 'last_twelve_hours',
        getRange: () => {
            const start = moment().subtract(12, 'hours');
            const end = moment();

            return {
                startTime: start.valueOf(),
                endTime: end.valueOf(),
            };
        },
    },
    today: {
        value: getTranslation('filtersRangesToday'),
        key: 'today',
        getRange: () => {
            const start = moment().startOf('day');
            const end = moment();

            return {
                startTime: start.valueOf(),
                endTime: end.valueOf(),
            };
        },
    },
    yesterday: {
        value: getTranslation('filtersRangesYesterday'),
        key: 'yesterday',
        getRange: () => {
            const start = moment().subtract(1, 'day').startOf('day');
            const end = moment().subtract(1, 'day').endOf('day');

            return {
                startTime: start.valueOf(),
                endTime: end.valueOf(),
            };
        },
    },
    thisWeek: {
        value: getTranslation('filtersRangesThisWeek'),
        key: 'thisWeek',
        getRange: () => {
            const start = moment().startOf('isoWeek');
            const end = moment();

            return {
                startTime: start.valueOf(),
                endTime: end.valueOf(),
            };
        },
    },
    lastWeek: {
        value: getTranslation('filtersRangesLastWeek'),
        key: 'lastWeek',
        getRange: () => {
            const start = moment().subtract(1, 'weeks').startOf('isoWeek');
            const end = moment().subtract(1, 'weeks').endOf('isoWeek');

            return {
                startTime: start.valueOf(),
                endTime: end.valueOf(),
            };
        },
    },
};

const orders = [
    {
        value: 1,
        label: getTranslation('resultsFiltersOrdersByTime'),
    },
    {
        value: 2,
        label: getTranslation('resultsFiltersOrdersByLeague'),
    },
];

export default function Results() {
    const initialFilter = {
        search: '',
        sportTypeId: '1:sr:sport:1',
        categoryId: '',
        leagueId: '',
        startDate: periods.lastTwelveHours.getRange().startTime,
        endDate: periods.lastTwelveHours.getRange().endTime,
        orderId: orders[0].id,
    };
    const [filter, setFilter] = useState(initialFilter);
    const [pageNumber, setPageNumber] = useState(0);
    const { data, loading, hasMore } = useSelector(state => state.results);
    const dispatch = useDispatch();
    const { ref, isIntersecting: isVisible } = useOnScreen();

    useEffect(() => {
        dispatch(getResults(filter));

        return () => {
            dispatch(clearMatchResults());
        };
    }, []);

    useEffectSkipFirst(() => {
        dispatch(clearMatchResults());
        dispatch(getResults(filter, 0));
        setPageNumber(0);
    }, [filter]);

    useEffect(() => {
        if (!loading && isVisible && hasMore) {
            setPageNumber(page => page + 1);
        }
    }, [isVisible]);

    useEffect(() => {
        if (pageNumber > 0 && !loading) {
            if (hasMore) {
                dispatch(getResults(filter, pageNumber));
            } else {
                setPageNumber(0);
            }
        }
    }, [pageNumber]);

    const onChangeFilter = (filterType, selectedItem) => {
        setPageNumber(0);
        if (filterType === 'sport') {
            setFilter(filter => ({ ...filter, sportTypeId: selectedItem.value, leagueId: '' }));
        } else if (filterType === 'category') {
            if (selectedItem.value === 'all') {
                setFilter(filter => ({ ...filter, categoryId: '' }));
            } else {
                setFilter(filter => ({ ...filter, categoryId: selectedItem.value }));
            }
        } else if (filterType === 'league') {
            if (selectedItem.value === 'all') {
                setFilter(filter => ({ ...filter, leagueId: '' }));
            } else {
                setFilter(filter => ({ ...filter, leagueId: selectedItem.value }));
            }
        } else if (filterType === 'period') {
            const { startTime, endTime } = selectedItem.getRange();
            setFilter(filter => ({ ...filter, startDate: startTime, endDate: endTime }));
        } else if (filterType === 'order') {
            setFilter(filter => ({ ...filter, orderId: selectedItem.value }));
        }
    };

    return (
        <div className={styles.results}>
            <Title title={getTranslation('title.results')} hasSearch />
            <ResultsFilter
                changeFilter={onChangeFilter}
                filterParams={filter}
                periods={Object.values(periods)}
                orders={orders}
            />
            {loading && <ContentSpinner />}
            <div className={styles.content}>
                {data.results.length > 0 && <ResultsTable data={data} />}
                <button className={'v-hidden h-0'} ref={ref}>
                    Load More
                </button>
            </div>
        </div>
    );
}
